import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DataTable from 'components/DataTable';
import { handleErrorResponse, parseJSON } from 'utils/general';
import { deleteDatum, getData } from 'api/general';
import { useAuth } from 'contexts/auth';
import { withLoading } from 'utils/hoc';
import MKBox from 'components/MaterialKit/MKBox';
import MKTypography from 'components/MaterialKit/MKTypography';
import { getBrand } from 'api/monitors';
import { datetimeFormatter } from 'utils/datetime';
import { InputBase, Paper } from '@mui/material';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import SearchIcon from '@mui/icons-material/Search';
import Button from 'components/Button';

const WatchOneModelTableSection = ({
  columns_config,
  page_options,
  table_name,
  id_field,
  extra_params,
  add_data_path,
  edit_data_path,
  addable,
  editable,
  deletable,
  add_button_label,
  edit_button_label,
  delete_button_label,
  odd_row_background_color,
  even_row_background_color,
  odd_row_font_color,
  even_row_font_color,
  header_background_color,
  header_font_color,
  title_color,
  title_font_size,
  setLoading,
  ...props
}) => {
  const { setAuth } = useAuth();
  const [rowsData, setRowsData] = useState(null);

  const [pageSize, setPageSize] = useState(page_options?.length ? page_options[0] : 10);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const brandId = searchParams.get('brand_id');
  const [brand, setBrand] = useState();
  const [columns, setColumns] = useState([]);
  const [keyword, setKeyword] = useState();
  const [intermediateKeyword, setIntermediateKeyword] = useState('');

  useEffect(() => {
    if (columns_config) {
      setColumns((columns_config || []).map((column) => {
        const res = { ...column };
        if (column.field.includes('date')) {
          res.formatter = datetimeFormatter;
        }
        return res;
      }));
    }
  }, [columns_config]);

  const fetchFromApi = useCallback((searchKeyword) => {
    const dataParams = {
      ...extra_params,
      ...(sortBy ? {
        $orderBy: `${sortBy} ${sortDirection}`,
      } : {}),
      $skip: currentPage * pageSize,
      $top: pageSize,
      brand: brandId,
      ...(searchKeyword ? {
        'keyword': searchKeyword,
      } : {}),
    };
    setLoading(true);
    return getData(`/${table_name}`, dataParams, 'bearer')
      .then(({ data, headers }) => {
        const contentRange = get(headers, 'content-range');
        const size = Number(contentRange.split('/')[1]);
        setRowsData(data);
        setTotalRows(size);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [extra_params, sortBy, sortDirection, currentPage, pageSize, brandId, setLoading, table_name, setAuth]);

  const deleteFromApi = useCallback((dataId) => {
    setLoading(true);
    return deleteDatum(`/${table_name}`, dataId, 'bearer')
      .then(() => {
        return fetchFromApi(keyword);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [table_name, fetchFromApi, setLoading, setAuth, keyword]);

  const onClickAdd = useCallback(() => {
    if (add_data_path && brandId) {
      navigate(`${add_data_path}?brand_id=${brandId}`);
    }
  }, [add_data_path, navigate, brandId]);

  const onClickEdit = useCallback((dataId) => {
    if (edit_data_path && brandId) {
      navigate(`${edit_data_path}?brand_id=${brandId}&${id_field}=${dataId}`);
    }
  }, [id_field, edit_data_path, navigate, brandId]);

  const onClickSort = useCallback((headerName) => {
    setSortBy(headerName);
    setSortDirection((previousSortDirection) => (previousSortDirection === 'asc' ? 'desc' : 'asc'));
  }, []);

  const fetchBrandFromApi = useCallback(() => {
    setLoading(true);
    return getBrand(brandId)
      .then(({ data }) => {
        setBrand(data);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [brandId, setLoading, setAuth]);

  const handleSearchKeyword = () => {
    setKeyword(intermediateKeyword);
    setCurrentPage(0);
  };

  useEffect(() => {
    if (table_name && brandId) {
      fetchFromApi(keyword);
    }
  }, [fetchFromApi, table_name, brandId, keyword]);

  useEffect(() => {
    if (brandId) {
      fetchBrandFromApi();
    }
  }, [fetchBrandFromApi, brandId]);

  return (
    <MKBox
      {...props}
    >
      <MKBox mb={2} display="flex" alignItems="center" justifyContent="space-between">
        <MKTypography mr="1rem" color={title_color} fontSize={title_font_size}>
          {brand && `${brand?.name} > Product [${totalRows}]`}
        </MKTypography>
        <Button onClick={() => brandId && navigate(`/product_management/brands?brand_id=${brandId}`)}>
          Edit Brand
        </Button>
      </MKBox>
      <MKBox
        display="flex"
        width="100%"
        justifyContent="space-between"
        alignItems="center"
        marginY={1}
      >
        <Paper
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSearchKeyword();
          }}
          sx={{ p: 0.5, display: 'flex', alignItems: 'center', width: '100%' }}
        >
          <SearchIcon fontSize="medium" style={{ marginLeft: '1rem' }} />
          <InputBase
            sx={{ ml: 2, flex: 1 }}
            placeholder="Search by keywords"
            onChange={(e) => setIntermediateKeyword(e.target.value)}
          />
          <KeyboardReturnIcon fontSize="medium" style={{ cursor: 'pointer', marginRight: '1rem' }} onClick={handleSearchKeyword} />
        </Paper>
      </MKBox>
      <DataTable
        data={rowsData}
        columns={columns}
        headerBackgroundColor={header_background_color}
        headerFontColor={header_font_color}
        oddRowBackgroundColor={odd_row_background_color}
        oddRowFontColor={odd_row_font_color}
        evenRowBackgroundColor={even_row_background_color}
        evenRowFontColor={even_row_font_color}
        idField={id_field}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onSort={onClickSort}
        onClickRow={editable ? onClickEdit : null}
        onPressAdd={addable ? onClickAdd : null}
        onPressEdit={editable ? onClickEdit : null}
        onPressDelete={deletable ? deleteFromApi : null}
        selectable
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        rowsPerPage={pageSize}
        rowsPerPageOptions={page_options}
        totalCount={totalRows}
        onRowsPerPageChange={setPageSize}
      />
    </MKBox>
  );
};

WatchOneModelTableSection.propTypes = {
  page_options: PropTypes.arrayOf(
    PropTypes.object,
  ).isRequired,
  table_name: PropTypes.string.isRequired,
  id_field: PropTypes.string,
  extra_params: PropTypes.object,
  add_data_path: PropTypes.string,
  edit_data_path: PropTypes.string,
  columns_config: PropTypes.array.isRequired,
  addable: PropTypes.bool,
  editable: PropTypes.bool,
  deletable: PropTypes.bool,
  add_button_label: PropTypes.string,
  edit_button_label: PropTypes.string,
  delete_button_label: PropTypes.string,
  setLoading: PropTypes.func,
  odd_row_background_color: PropTypes.string,
  even_row_background_color: PropTypes.string,
  odd_row_font_color: PropTypes.string,
  even_row_font_color: PropTypes.string,
  header_background_color: PropTypes.string,
  header_font_color: PropTypes.string,
  title_color: PropTypes.string,
  title_font_size: PropTypes.string,
};

export default withLoading(WatchOneModelTableSection);
